/**
 * © 2026 Madar.iq — جميع الحقوق محفوظة
 * هذا الملف جزء من منظومة Madar.iq البرمجية.
 * يُحظر نسخه أو تعديله أو توزيعه دون إذن كتابي.
 * All Rights Reserved — Unauthorized use is prohibited.
 */
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NavbarLangOnly from "../../components/Home/navbarLang/NavbarLangOnly";
import { ToastContainer } from "react-toastify";
import { useAuth } from "../../context/auth/AuthContext";
import PaymentSuccessUI from "./PaymentSuccessUI";
import { saveTenantPlan } from "../utils/saveTenantPlan";

export default function PaymentSuccess() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(search);
  const status = params.get("status");
  const { i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const { login: authLogin } = useAuth();

  const [isMobile, setIsMobile] = useState(window.innerWidth < 576);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  const baseUrl = (process.env.REACT_APP_API_BASE_URL || "https://madar-api.madar.iq/api").replace(/\/+$/, "");

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 576);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const COLORS = {
    darkPurple: "#21003D",
    purple: "#7400BF",
    gold: "#F2B441",
    gradient: "linear-gradient(135deg, #21003D, #7400BF)",
    textLight: "#f3e9ff",
  };

  useEffect(() => {
    const tenantId = parseInt(localStorage.getItem("tenantId") || "0");
  console.log(tenantId);
  
    const permissions = {
      viewOrders: 1, createOrder: 1, editOrder: 1, deleteOrder: 1,
      viewProducts: 1, createProduct: 1, editProduct: 1, deleteProduct: 1,
      viewStock: 1, viewDivisions: 1, addDivisions: 1, editDivisions: 1, deleteDivisions: 1,
      viewCategories: 1, addCategories: 1, editCategories: 1, deleteCategories: 1,
      viewCustomers: 1, addCustomers: 1, editCustomers: 1, deleteCustomers: 1,
      viewBanners: 1, addBanners: 1, editBanners: 1, deleteBanners: 1,
      viewTheme: 1, editTheme: 1,
      viewStoreSettings: 1, editStoreSettings: 1,
      viewCoupons: 1, addCoupons: 1, editCoupons: 1, deleteCoupons: 1,
      viewActivity: 1, viewSubscriptions: 1, editSubscriptions: 1,
      viewNotification: 1, addNotification: 1,
      viewPaymentMethods: 1, addPaymentMethods: 1, editPaymentMethods: 1, deletePaymentMethods: 1,
      viewShipping: 1, addShipping: 1, editShipping: 1, deleteShipping: 1,
      viewInvoice: 1, editInvoice: 1,
      viewAnalytics: 1, viewSalesReport: 1, viewInventoryReport: 1,
      viewCustomerReport: 1, viewProductReport: 1,
      viewEmployees: 1, addEmployees: 1, editEmployees: 1, deleteEmployees: 1,
    };

    authLogin({
      token: localStorage.getItem("authToken"),
      user: JSON.parse(localStorage.getItem("user") || "{}"),
      authRole: "tenant",
      isTenantOwner: true,
      permissions,
    });
  }, []);

  useEffect(() => {
    const checkPaymentStatus = async () => {
      const savedPaymentStatus = localStorage.getItem("paymentStatus");
      if (savedPaymentStatus === "success") {
        setPaymentStatus(true);
        setLoading(false);
        localStorage.removeItem("paymentStatus");
        return;
      }

      if (status === "success") {
        setPaymentStatus(true);
        setLoading(false);
        return;
      }

      if (status === "failed") {
        setPaymentStatus(false);
        setLoading(false);
        return;
      }

      try {
        const tenantId = localStorage.getItem("tenantId");
        const token = localStorage.getItem("authToken");

        if (!tenantId) {
          setPaymentStatus(false);
          setLoading(false);
          return;
        }

        const res = await fetch(
          `${baseUrl}/GetTenantPayment?tenantId=${tenantId}`,
          {
            headers: {
              Authorization: token ? `Bearer ${token}` : "",
            },
          }
        );

        const data = await res.json().catch(() => []);

        if (res.ok && Array.isArray(data) && data.length > 0) {
          const latest = data[0];
          const isPaid =
            latest.TransactionStatus === "paid" ||
            latest.TransactionStatus === "success" ||
            latest.Status === "paid" ||
            latest.Status === "success";
          setPaymentStatus(isPaid);
        } else {
          setPaymentStatus(status === "success");
        }
      } catch {
        setPaymentStatus(status === "success");
      } finally {
        setLoading(false);
      }
    };

    checkPaymentStatus();
  }, [status, baseUrl]);

  const isSuccess = paymentStatus === true;

  useEffect(() => {
    if (!isSuccess) return;

    const persistPlanAfterPayment = async () => {
      const planId = parseInt(localStorage.getItem("selectedPlanId") || "0", 10);
      if (!planId) return;

      try {
        const plan = {
          planId,
          title: localStorage.getItem("selectedPlanType") || "",
          engTitle: localStorage.getItem("selectedEngPlanType") || "",
          price: parseFloat(localStorage.getItem("selectedPlanPrice") || "0"),
          desc: localStorage.getItem("selectedPlanDesc") || "",
        };
        await saveTenantPlan(plan, isArabic);

        const nowIso = new Date().toISOString();
        localStorage.setItem("paidStartDate", nowIso);
        localStorage.setItem("subscriptionStartDate", nowIso);
        localStorage.setItem("paymentStatus", "success");
        localStorage.removeItem("force_expiry_notice");
        sessionStorage.removeItem("trialExpiredAlertShown");
        sessionStorage.removeItem("paidExpiredAlertShown");

        const now = new Date();
        localStorage.setItem("plan_renewed_at", now.toISOString());
        const expiryDate = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);
        localStorage.setItem("plan_expiry_date", expiryDate.toISOString());
      } catch (err) {
        console.error("Failed to save tenant plan after payment:", err);
      }
    };

    persistPlanAfterPayment();
  }, [isSuccess, isArabic]);

  useEffect(() => {
    if (paymentStatus === null) return;

    const timer = setTimeout(() => {
      const isUpgrade =
        new URLSearchParams(window.location.search).get("upgrade") === "true";

      if (isSuccess) {
        navigate(isUpgrade ? "/admin-store/subscriptions" : "/admin-store");
      } else {
        navigate("/payment");
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, [isSuccess, paymentStatus, navigate]);

  const handleRedirect = () => {
    const isUpgrade =
      new URLSearchParams(window.location.search).get("upgrade") === "true";

    if (isSuccess) {
      navigate(isUpgrade ? "/admin-store/subscriptions" : "/admin-store");
    } else {
      navigate("/payment");
    }
  };

  return (
    <>
      <NavbarLangOnly />
      <ToastContainer />
      <PaymentSuccessUI
        isArabic={isArabic}
        isMobile={isMobile}
        loading={loading}
        isSuccess={isSuccess}
        COLORS={COLORS}
        handleRedirect={handleRedirect}
      />
    </>
  );
}
